import { useState } from "react";

import type { ApiErrorBody } from "../../api/client";
import { capDiff, capLabel, type McpQuotaAll } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { ForbiddenNotice, Gate } from "../authority/Gate";
import { Badge, Notice } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { Button, Select } from "../../f7";

interface TierResponse extends ApiErrorBody {
  tier?: {
    tier?: string;
    previous_tier?: string;
    tier_source?: string;
  };
  blocklist?: unknown;
}

/**
 * Change a merchant's plan tier.
 *
 * Sends ONLY `tier` — never alongside `blocked`. The endpoint accepts both and
 * is not atomic across them (tier lives in entitlements keyed by business_id,
 * the block in the blocklist keyed by business_reference; no transaction spans
 * both). One field means one write and one outcome, so partial application
 * stops being reachable.
 */
export function TierControl({
  config,
  reference,
  currentTier,
  knownTiers,
  onDone,
}: {
  config: AppConfig;
  reference: string;
  /** `effective_tier` — what the server actually applies today. */
  currentTier: string;
  knownTiers: string[];
  onDone: () => void;
}) {
  const write = useWrite<TierResponse>(config);
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [result, setResult] = useState<TierResponse | null>(null);

  const caps = quota.state.kind === "ready" ? quota.state.data.caps : null;
  const changes =
    caps && target && caps[currentTier] && caps[target]
      ? capDiff(caps[currentTier]!, caps[target]!)
      : [];

  function submit(reason: string) {
    const body: Record<string, unknown> = { tier: target };
    if (reason) body.reason = reason;

    void write
      .submit(`/mcp/admin/tenant/${encodeURIComponent(reference)}`, {
        method: "POST",
        body: JSON.stringify(body),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setOpen(false);
        setTarget("");
        onDone();
      });
  }

  const options = knownTiers.filter((t) => t !== currentTier);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* The Select is NOT gated — picking a target tier writes nothing, and
            an operator who cannot apply the change can still see which tiers
            exist. Gate wraps the one control that writes, which is also what
            lets it put `aria-disabled` on a real control rather than on a
            container. */}
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label="Change tier to"
          >
            <option value="">Change tier to…</option>
            {options.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <Gate need="tenant:write">
            {/* `disabled` rather than an aria-disabled button painted grey by
                hand: F7 already draws the disabled state, and the hand-rolled
                one was still in the tab order while being unclickable. */}
            <Button onClick={() => setOpen(true)} disabled={!target}>
              Review change
            </Button>
          </Gate>
        </div>
      </div>

      {write.state.kind === "denied" && (
        <div className="mt-2">
          <ForbiddenNotice message={write.state.error.message} />
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mt-2">
          <Notice tone="red" title="Refused — nothing was written">
            {write.state.error.message}
          </Notice>
        </div>
      )}

      {result?.tier && (
        <div className="mt-3">
          <WriteResult title="Tier changed" onDismiss={() => setResult(null)}>
            <p>
              <span className="ref">{result.tier.previous_tier ?? "—"}</span> →{" "}
              <span className="ref">{result.tier.tier}</span>. The entitlements row is
              updated immediately, but the merchant's effective caps only change on their{" "}
              <strong>next gated call</strong> — nothing pushes it to a session already in
              flight.
            </p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={`Change ${reference} to ${target}`}
        confirmLabel="Change tier"
        requireReason
        reasonHint="e.g. upgraded per sales"
        busy={write.state.kind === "submitting"}
        onCancel={() => setOpen(false)}
        onConfirm={submit}
        effect="The entitlements row changes immediately. The merchant's effective caps change on their next gated call — nothing pushes it to a session already in flight."
      >
        <p>
          <span className="ref">{currentTier}</span> → <span className="ref">{target}</span>
        </p>

        {/* The cap diff turns an opaque label swap into a statement of what
            the merchant will actually experience. */}
        {quota.state.kind === "loading" && <p className="text-xs">Loading caps…</p>}
        {caps && changes.length === 0 && target && (
          <p className="text-xs">
            No cap differs between these tiers — the change is commercial only.
          </p>
        )}
        {changes.length > 0 && (
          <div>
            <p className="text-xs font-medium">What changes for this merchant:</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {changes.map((c) => (
                <li key={c.tool}>
                  <span className="ref">{c.tool}</span>{" "}
                  <Badge tone={c.from === undefined ? "neutral" : capLabel(c.from).tone}>
                    {c.from === undefined ? "unset" : capLabel(c.from).text}
                  </Badge>{" "}
                  →{" "}
                  <Badge tone={c.to === undefined ? "neutral" : capLabel(c.to).tone}>
                    {c.to === undefined ? "unset" : capLabel(c.to).text}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ConfirmDialog>
    </>
  );
}
